import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpError, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OrderFlowAuthService } from '../../auth/order-flow-auth.service';
import { NGXLogger } from 'ngx-logger';
import {
  EmailSentEventDetails,
  OrderStatusUpdatedEventDetails,
  OrderUpdatedPayload
} from '../../orders/hub-events.model';

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly authService = inject(OrderFlowAuthService);
  private readonly logger = inject(NGXLogger);

  private readonly hubUrl = `${environment.apiEndpoint}/hubs/orders`;
  private connection: HubConnection | null = null;
  private initialized = false;
  private stopped = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // Incremented each time a new HubConnection is built. Handlers capture their version at
  // registration time and return early if it no longer matches, preventing stale connections
  // from scheduling restarts or emitting state changes after they have been replaced.
  private connectionVersion = 0;

  // Counts manual restart attempts (i.e. the outer retry loop that runs outside of
  // withAutomaticReconnect). Reset to 0 on any successful connect/reconnect.
  private manualRestartCount = 0;

  readonly connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);

  private readonly _orderUpdated$ = new Subject<OrderUpdatedPayload>();
  private readonly _emailSent$ = new Subject<EmailSentEventDetails>();
  private readonly _orderStatusUpdated$ = new Subject<OrderStatusUpdatedEventDetails>();

  readonly orderUpdated$: Observable<OrderUpdatedPayload> = this._orderUpdated$.asObservable();
  readonly emailSent$: Observable<EmailSentEventDetails> = this._emailSent$.asObservable();
  readonly orderStatusUpdated$: Observable<OrderStatusUpdatedEventDetails> = this._orderStatusUpdated$.asObservable();

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.reconnectIfDisconnected('visibility-change');
    }
  };

  private readonly onNetworkOnline = () => {
    this.reconnectIfDisconnected('network-online');
  };

  /**
   * Call once after the user is authenticated and the app is ready.
   * Safe to call multiple times (idempotent).
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onNetworkOnline);
    await this.startNewConnection();
  }

  // Immediately reconnect if the connection is currently disconnected.
  // Called proactively when the tab regains focus or the network comes back.
  private reconnectIfDisconnected(trigger: string): void {
    if (this.stopped) return;
    if (this.connection?.state === HubConnectionState.Disconnected) {
      this.logger.info(`[SignalR] Proactive reconnect triggered by ${trigger}`);
      this.clearRetryTimer();
      this.startNewConnection();
    }
  }

  // Builds a fresh HubConnection. Each call increments connectionVersion so that
  // handlers registered on the previous connection object become inert.
  private async startNewConnection(): Promise<void> {
    if (this.stopped) return;
    const version = ++this.connectionVersion;
    const conn = this.buildConnection();
    this.connection = conn;
    this.registerHandlers(conn, version);
    await this.start(conn, version);
  }

  private buildConnection(): HubConnection {
    // All environments negotiate through Azure SignalR Service, which terminates the actual
    // WebSocket connection directly rather than routing it through Front Door/App Service.
    // That removes the prod-only Front Door affinity dependency and the UAT/staging
    // LongPolling-only workaround alike, so every environment can just negotiate normally:
    // SignalR tries WebSockets first and falls back automatically if they're blocked.
    const isProduction = environment.env === 'production';
    return (
      new HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => this.acquireToken()
        })
        .withAutomaticReconnect({
          // Built-in reconnect loop (handles network drops after initial connect).
          // accessTokenFactory is called on every attempt so MSAL always provides a fresh token.
          // Never returns null so the loop retries indefinitely.
          nextRetryDelayInMilliseconds: (ctx) => {
            const delays = [0, 2_000, 10_000, 30_000];
            return delays[ctx.previousRetryCount] ?? 60_000;
          }
        })
        // These must match KeepAliveInterval / ClientTimeoutInterval on the server.
        .withKeepAliveInterval(15_000)
        .withServerTimeout(30_000)
        .configureLogging(isProduction ? LogLevel.Warning : LogLevel.Information)
        .build()
    );
  }

  // Wrapper around getAccessToken$ that throws on an empty result.
  // Throwing here causes SignalR to fail the connection attempt without ever sending a
  // request to the server, preventing spurious 401 entries in server logs.
  // An empty token is returned by getAccessToken$ when MSAL has an interaction_in_progress
  // error; SignalR's retry loop will call this again after its next delay interval.
  private async acquireToken(): Promise<string> {
    const token = await lastValueFrom(this.authService.getAccessToken$());
    if (!token) {
      throw new Error('[SignalR] Token acquisition returned empty — MSAL interaction in progress, will retry');
    }
    return token;
  }

  private registerHandlers(conn: HubConnection, version: number): void {
    conn.on('orderUpdated', (payload: OrderUpdatedPayload) => this._orderUpdated$.next(payload));
    conn.on('emailSent', (details: EmailSentEventDetails) => this._emailSent$.next(details));
    conn.on('orderStatusUpdated', (details: OrderStatusUpdatedEventDetails) => this._orderStatusUpdated$.next(details));

    conn.onreconnecting((err) => {
      if (this.connectionVersion !== version) return;
      this.connectionState$.next(HubConnectionState.Reconnecting);
      // A 401 here is expected for LongPolling: each poll is a separate HTTP request,
      // so an expired access token causes the next poll to return 401. SignalR catches it
      // and calls accessTokenFactory again with a fresh token before retrying.
      this.logger.warn('[SignalR] Reconnecting — cause:', err?.message ?? 'unknown');
    });

    conn.onreconnected(() => {
      if (this.connectionVersion !== version) return;
      this.manualRestartCount = 0;
      this.connectionState$.next(HubConnectionState.Connected);
      this.logger.info('[SignalR] Reconnected');
    });

    conn.onclose((err) => {
      if (this.connectionVersion !== version) return;
      this.connectionState$.next(HubConnectionState.Disconnected);
      if (err) {
        this.logger.error('[SignalR] Connection closed with error', err.message);
      } else {
        // Server-initiated graceful close (e.g. deployment restart). Schedule reconnect
        // so the client recovers automatically once the server is back.
        this.logger.warn('[SignalR] Connection closed cleanly — server may have restarted');
      }
      this.scheduleRestart(this.isAuthError(err));
    });
  }

  private isAuthError(err: Error | undefined): boolean {
    if (!err) return false;
    const msg = err.message?.toLowerCase() ?? '';
    // Match server-side 401/auth rejections only. 'token' is intentionally excluded — our
    // own acquireToken() error message contains 'token' and would incorrectly match here,
    // causing scheduleRestart() to wait 10s when progressive backoff would be more appropriate.
    return (
      msg.includes('401') || msg.includes('unauthorized') || msg.includes('security token') || msg.includes('expired')
    );
  }

  private scheduleRestart(isAuthRelated: boolean): void {
    if (this.stopped) return;
    this.clearRetryTimer();
    // Auth errors need more time so MSAL can refresh via its own silent/popup flow before
    // we call acquireToken() again. Other errors use a progressive backoff.
    const delay = isAuthRelated ? 10_000 : this.nextManualRestartDelay();
    this.logger.info(`[SignalR] Scheduling reconnect in ${delay / 1_000}s (auth-related: ${isAuthRelated})`);
    this.retryTimer = setTimeout(() => {
      this.manualRestartCount++;
      this.startNewConnection();
    }, delay);
  }

  // Progressive backoff for the outer manual restart loop (separate from withAutomaticReconnect).
  private nextManualRestartDelay(): number {
    const delays = [3_000, 10_000, 30_000];
    return delays[this.manualRestartCount] ?? 60_000;
  }

  private async start(conn: HubConnection, version: number): Promise<void> {
    if (this.stopped || this.connectionVersion !== version) return;
    try {
      await conn.start();
      if (this.connectionVersion !== version) return;
      this.manualRestartCount = 0;
      this.connectionState$.next(HubConnectionState.Connected);
      this.logger.info('[SignalR] Connected');
    } catch (err: unknown) {
      if (this.connectionVersion !== version) return;
      this.connectionState$.next(HubConnectionState.Disconnected);
      const errorForLog = err instanceof Error ? err.message : err;
      this.logger.error('[SignalR] Failed to connect', errorForLog);
      const is404 =
        (err instanceof Error && err.message.includes('404')) || (err instanceof HttpError && err.statusCode === 404);
      if (!is404) {
        this.scheduleRestart(this.isAuthError(err instanceof Error ? err : undefined));
      }
    }
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopped = true;
    // Invalidate all pending handlers and timers from any live connection version.
    this.connectionVersion++;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onNetworkOnline);
    this.clearRetryTimer();
    this.connection?.stop();
  }
}
